// What a loading screen says is happening: the steps of a view's load,
// each with its state, as pure functions of the state the view's store
// holds. A step is a read the runtime really runs (a lane's `Remote`, or a
// stretch of one read that says how far it is), so nothing here counts
// time or guesses:
//
// - `waiting`: not asked for yet; `active`: being read; `done`: read;
// - `held`: it failed and the runtime will try it again by itself (a rate
//   limit's end); `failed`: it failed and waits for the user.
//
// A step has `progress` only where a real fraction is known (files listed
// of the files changed, comments read of the threads' counts). `needed`
// steps are what the view waits on; the others go on after it opens, as
// they do without a loading screen.
import type { Failure } from '../api/failure';
import type { Section } from '../logic/sections';
import type { PrTab } from '../router';
import { pendingAt } from '../runtime/effects';
import { manual, type Remote, type Retry } from '../runtime/remote';
import type { DashState, Slot } from '../state/dashboard/program';
import type { Fraction } from '../state/pr/fetch';
import { failureIn, serverData, type PrLoads } from '../state/pr/program';

export type { Fraction };

export type StepStatus = 'waiting' | 'active' | 'done' | 'held' | 'failed';

export interface Step {
  id: string;
  label: string;
  status: StepStatus;
  /** A count or a note beside the label ("52", "200 so far"); null for none. */
  detail: string | null;
  /** How far an active step is, where that is a known fraction. */
  progress: Fraction | null;
  /** Whether the view waits on it. */
  needed: boolean;
}

/** The step that stopped a load: why, and how it is tried again. */
export interface Stopped {
  step: string;
  /** What was loading, as `statusOf` names it ("this pull request"). */
  what: string;
  failure: Failure;
  retry: Retry;
}

export interface LoadView {
  /** What is loading: "empathic/pathbase #499". */
  title: string;
  steps: Step[];
  /** The needed step that failed, when one did. */
  stopped: Stopped | null;
}

const step = (id: string, label: string, status: StepStatus, over: Partial<Step> = {}): Step => ({ id, label, status, detail: null, progress: null, needed: true, ...over });

/** A failure's status: held while its retry by itself is pending. */
const failedAs = (retry: Retry): StepStatus => (pendingAt(retry) === null ? 'failed' : 'held');

/** A lane's status. What it shows counts as done, whatever its refresh is doing. */
function statusOf(r: Remote<unknown>): StepStatus {
  switch (r.tag) {
    case 'NotAsked':
      return 'waiting';
    case 'Loading':
      return 'active';
    case 'Failed':
      return failedAs(r.retry);
    case 'Has':
      return 'done';
  }
}

/** A fraction worth showing: a whole of something, not yet overrun. */
const fraction = (done: number | null, total: number | undefined): Fraction | null =>
  done !== null && total !== undefined && total > 0 && done <= total ? { done, total } : null;

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The first needed step that failed, as what stopped the load. */
function stoppedAt(steps: Step[], failures: Record<string, { what: string; in: Remote<unknown> }>): Stopped | null {
  for (const s of steps) {
    if (!s.needed || (s.status !== 'failed' && s.status !== 'held')) continue;
    const f = failures[s.id];
    const found = f && failureIn(f.in);
    if (found) return { step: s.id, what: f.what, failure: found.reason, retry: found.retry };
  }
  return null;
}

export interface PrTarget {
  owner: string;
  repo: string;
  number: number;
}

export const prTitle = (at: PrTarget) => `${at.owner}/${at.repo} #${at.number}`;

/**
 * The pull request page's load, for the view of `tab`: the pull request's
 * own read in the stretches it says it is at (its first answer, then the
 * further pages of threads, timeline and checks), which every tab waits
 * on; the changed files, which the Files tab waits on; then the viewer's
 * drafts and the stack, which no tab does.
 */
export function prSteps(s: PrLoads, at: PrTarget, tab: PrTab): LoadView {
  const pr = s.server.pr;
  const lane = pr ? 'done' : statusOf(s.pr);
  const p = s.prProgress;
  // Past its first answer the read says which stretch it is at; before it, only the first is under way.
  const order = ['threads', 'timeline', 'checks'] as const;
  const stretch = (name: (typeof order)[number]): StepStatus => {
    if (lane !== 'active') return lane === 'done' ? 'done' : 'waiting';
    if (!p) return 'waiting';
    const here = order.indexOf(p.reading);
    const mine = order.indexOf(name);
    return mine < here ? 'done' : mine === here ? 'active' : 'waiting';
  };
  const first: StepStatus = lane === 'active' ? (p ? 'done' : 'active') : lane;
  const threads = stretch('threads');
  const timeline = stretch('timeline');
  const checks = stretch('checks');
  const steps: Step[] = [
    step('pr', 'Pull request', first),
    step('threads', 'Review threads', threads, {
      progress: threads === 'active' ? fraction(p?.comments?.done ?? null, p?.comments?.total) : null,
      detail:
        threads === 'active' && p
          ? p.comments
            ? `${p.comments.done} of ${count(p.comments.total, 'comment')}`
            : `${p.threads} so far`
          : pr
            ? String(s.server.threads.length)
            : null,
    }),
    step('timeline', 'Timeline', timeline, { detail: timeline === 'active' && p ? `${count(p.timeline, 'event')} so far` : null }),
    step('checks', 'Checks', checks, {
      progress: checks === 'active' ? fraction(p?.checks?.done ?? null, p?.checks?.total) : null,
      detail: checks === 'active' && p?.checks ? `${p.checks.done} of ${p.checks.total}` : null,
    }),
  ];
  const files = statusOf(s.files);
  const changed = pr?.changedFiles;
  const listed = fraction(s.filesListed, changed);
  steps.push(
    step('files', 'Changed files', files, {
      needed: tab === 'files',
      progress: files === 'active' ? listed : null,
      detail: files === 'active' && listed ? `${listed.done} of ${listed.total}` : changed !== undefined ? String(changed) : null,
    }),
  );
  // Drafts are the changelist server's: not read where it keeps nothing for the repository.
  if (serverData(s)) steps.push(step('drafts', 'Your drafts', statusOf(s.drafts), { needed: false }));
  steps.push(step('stack', 'Stack', statusOf(s.stack), { needed: false }));
  return {
    title: prTitle(at),
    steps,
    stopped: stoppedAt(steps, {
      pr: { what: 'this pull request', in: s.pr },
      files: { what: "this pull request's changed files", in: s.files },
    }),
  };
}

/** The Files view's load once the pull request is in: the pull request, and its changed files. */
export function filesSteps(s: PrLoads, at: PrTarget): LoadView {
  const all = prSteps(s, at, 'files');
  const keep = new Set(['pr', 'files']);
  return { ...all, steps: all.steps.filter((x) => keep.has(x.id)) };
}

function sectionStep(section: Section, slot: Slot | undefined, i: number, attn: DashState['attn']): Step {
  const id = `section ${i}`;
  if (!slot) return step(id, section.title, 'waiting');
  const shown = slot.shown;
  if (shown.tag === 'Has') return step(id, section.title, 'done', { detail: String(shown.value.rows.length) });
  if (shown.tag === 'Failed') return step(id, section.title, failedAs(shown.retry));
  const flow = slot.flow;
  if (flow.at === 'searching') return step(id, section.title, 'active', { detail: 'searching' });
  if (flow.at === 'attention') {
    // Its rows are in; whose turn each is on is worked out row by row.
    const done = flow.prs.filter((p) => attn.get(p.id) === 'done').length;
    const progress = fraction(done, flow.prs.length);
    return step(id, section.title, 'active', { progress, detail: progress ? `${done} of ${count(flow.prs.length, 'pull request')}` : null });
  }
  return step(id, section.title, 'waiting');
}

/** The dashboard's load: each section's search, then its rows' attention. */
export function dashboardSteps(s: DashState): LoadView {
  return { title: 'Your dashboard', steps: s.sections.map((section, i) => sectionStep(section, s.slots[i], i, s.attn)), stopped: null };
}

/** Whether the dashboard has nothing to show yet: no section has rows or a failure of its own to say. */
export function dashboardCold(s: DashState): boolean {
  return s.sections.length > 0 && s.sections.every((_, i) => {
    const tag = s.slots[i]?.shown.tag;
    return tag === undefined || tag === 'Loading' || tag === 'NotAsked';
  });
}

/** A view whose load is one read: `label`, under way, done, or stopped by `failure`. */
export function oneStep(title: string, label: string, state: 'loading' | 'ready' | { failure: Failure; what: string }): LoadView {
  const failed = typeof state === 'object';
  return {
    title,
    steps: [step('load', label, failed ? 'failed' : state === 'ready' ? 'done' : 'active')],
    stopped: failed ? { step: 'load', what: state.what, failure: state.failure, retry: manual } : null,
  };
}

/**
 * What a polite live region says of a load: its title and the step under
 * way. It changes when another step begins, not as one counts up, and is
 * null once the load stopped (the failure notice says that itself) or has
 * nothing under way.
 */
export function announcement(v: LoadView): string | null {
  if (v.stopped) return null;
  const active = v.steps.find((s) => s.status === 'active' && s.needed) ?? v.steps.find((s) => s.status === 'active');
  return active ? `Loading ${v.title}: ${active.label.toLowerCase()}…` : null;
}
